import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button } from 'components/ui/Button'
import { ChevronLeft, ChevronRight, Minus, Plus, X } from 'lucide-react'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Serve the worker from our own origin (copied from pdfjs-dist on install).
// Avoids CDN/unpkg supply-chain risk and keeps worker version aligned with the package.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`

interface PDFViewerProps {
  fileUrl: string
  fileName: string
  onClose: () => void
}

const PDF_DOCUMENT_OPTIONS = {
  // Defense in depth for older/misaligned workers (CVE-2024-4367 uses eval/font path)
  isEvalSupported: false as const,
}

function PDFViewer({ fileUrl, fileName, onClose }: PDFViewerProps) {
  const { t } = useTranslation()
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1.1)

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    setNumPages(0)
    setPageNumber(1)

    let blobUrlToCleanup: string | null = null
    let cancelled = false

    const fetchPDF = async () => {
      try {
        const response = await fetch(fileUrl, {
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        blobUrlToCleanup = blobUrl

        if (!cancelled) {
          setPdfUrl(blobUrl)
          setIsLoading(false)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(`Failed to load PDF: ${err.message}`)
          setIsLoading(false)
        }
      }
    }

    fetchPDF()

    return () => {
      cancelled = true
      if (blobUrlToCleanup) {
        URL.revokeObjectURL(blobUrlToCleanup)
      }
    }
  }, [fileUrl])

  const fileSource = useMemo(() => pdfUrl, [pdfUrl])

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg shadow-2xl max-w-7xl h-[90vh] w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-4 bg-card rounded-t-lg shrink-0">
          <h2 className="text-lg font-semibold truncate flex-1 mr-2">{fileName}</h2>

          {numPages > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                title={t('common.previous', { defaultValue: 'Previous' })}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {pageNumber} / {numPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                disabled={pageNumber >= numPages}
                title={t('common.next', { defaultValue: 'Next' })}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale((s) => Math.max(0.6, Number((s - 0.1).toFixed(1))))}
                title={t('common.zoomOut', { defaultValue: 'Zoom out' })}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScale((s) => Math.min(2.5, Number((s + 0.1).toFixed(1))))}
                title={t('common.zoomIn', { defaultValue: 'Zoom in' })}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Button variant="outline" size="sm" onClick={onClose} title={t('common.close')}>
            <X className="h-4 w-4 mr-2" />
            {t('common.close')}
          </Button>
        </div>

        <div className="flex-1 overflow-auto rounded-b-lg min-h-0 bg-muted/30">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">{t('candidateDetail.loadingPdf')}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md p-6">
                <p className="text-destructive font-medium mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>
                  {t('candidateDetail.retry')}
                </Button>
              </div>
            </div>
          )}

          {!error && !isLoading && fileSource && (
            <div className="flex justify-center p-4">
              <Document
                file={fileSource}
                options={PDF_DOCUMENT_OPTIONS}
                loading={
                  <div className="py-16 text-muted-foreground">
                    {t('candidateDetail.loadingPdf')}
                  </div>
                }
                onLoadSuccess={({ numPages: nextNumPages }) => {
                  setNumPages(nextNumPages)
                  setPageNumber(1)
                }}
                onLoadError={(loadError) => {
                  setError(`Failed to render PDF: ${loadError.message}`)
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer
                  renderAnnotationLayer
                />
              </Document>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PDFViewer
